import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { contentStyles, h3, h4, h6, paragraphRegular } from '@helpers/typography';
import ArticleTagsStyles from '@stories/Components/Misc/ArticleTags/ArticleTags.styles';
import SectionWrapper, { ContentType, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --bgColour: ${brand.green.light};
  --baseThemeColour: ${brand.green.default};
  --buttonColour: ${brand.green.dark};
  --sectionSpacingTop: ${fluid(20, 26)};
  --sectionSpacingBottom: ${fluid(20, 28)};

  background-color: var(--bgColour);

  &[data-type=${ContentType.Intermediaries}] {
    --listColour: ${brand.blue.navy};
    --quoteColour: ${brand.green.dark};
    --bgColour: ${brand.green.light};
    --baseThemeColour: ${brand.blue.navy};
    --buttonColour: ${brand.green.dark};
  }

  &[data-type=${ContentType.Savings}] {
    --listColour: ${brand.blue.dark};
    --quoteColour: ${brand.blue.default};
    --bgColour: ${brand.blue.light};
    --baseThemeColour: ${brand.blue.default};
    --buttonColour: ${brand.blue.dark};
  }

  ${SectionWrapperStyles.Inner} {
    ${baseGrid};
  }
`;
const SideMenuArea = styled.div `
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1;
  padding: 8px 18px;
  background-color: ${rgba(brand.blue.navy, 0.5)};

  &[data-hide='1'] {
    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    position: static;
    grid-column: 1 / span 3;
    background-color: transparent;
    padding: 0;
  }
`;
const SideMenu = styled.div `
  background-color: ${brand.global.white};
  border-radius: ${fluid(10, 20)} ${fluid(10, 20)} ${fluid(10, 20)} 0;
  padding: 16px 12px;

  @media ${from(Device.TabletLarge)} {
    position: sticky;
    top: calc(var(--headerHeight, 0px) + 20px);
    padding: 36px 16px;
    box-shadow: 0px 0px 20.4px 0px rgba(0, 34, 64, 0.1);
  }
`;
const SideTitle = styled.h2 `
  ${h6};
`;
const ProgressBarHolder = styled.div `
  margin: 0;
  width: 100%;
  height: 9px;
  background-color: ${brand.grey.light};
  border-radius: 10px;
  position: relative;
  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    margin: 24px 0 29px;
  }
`;
const ProgressBar = styled.div `
  position: absolute;
  left: -100%;
  top: 0;
  height: 100%;
  width: 100%;
  background-color: var(--baseThemeColour);
  border-radius: 10px;

  transform: translateX(var(--progressAmount, 0));
`;
const ButtonList = styled.div `
  ${scrollbars};

  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 50dvh;
  overflow-y: auto;

  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    width: 11px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${brand.grey.light};
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const SectionButton = styled.button `
  ${ButtonReset};
  ${transition('background-color, color')};
  ${paragraphRegular};

  padding: 12px;
  text-decoration: none;
  margin: 0;
  border-radius: 10px;
  position: relative;
  text-align: left;
  padding-right: 50px;
  margin-right: 11px;

  &:focus-visible {
    outline: auto;
    outline-offset: -3px;
    outline-width: 2px;
    outline-color: ${brand.blue.navy};
  }

  &:hover {
    background-color: var(--bgColour);
  }

  &[data-active='1'] {
    ${fonts.proximaNova.semiBold};
    background-color: var(--buttonColour);
    color: ${brand.global.white};
  }
`;
const ActiveSvg = styled.span `
  ${transition('opacity')};

  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;

  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);

  border-radius: 50%;
  background-color: ${brand.global.white};
  opacity: 0;

  svg {
    color: var(--buttonColour);
    width: 15px;
  }

  [data-active='1'] & {
    opacity: 1;
  }
`;
const GuideContent = styled.div `
  grid-column: 1 / span 12;
  background-color: ${brand.global.white};
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  padding: ${fluid(20, 40)} 0;
  border-radius: 20px 20px 20px 0;
  box-shadow: 0px 0px 20.4px 0px rgba(0, 34, 64, 0.1);

  @media ${from(Device.Tablet)} {
    grid-column: 2 / -2;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 4 / span 9;
  }
`;
const GuideContentInner = styled.div `
  padding: 2rem;
  grid-column: 1 / -1;
  padding: 0 ${fluid(16, 73)};

  @media ${from(Device.TabletLarge)} {
    grid-column: 2 / -2;
  }
`;
const IntroBlock = styled.div `
  padding-left: 14px;
  margin-bottom: ${fluid(18, 48)};

  ${ArticleTagsStyles.Container} {
    margin-bottom: 18px;
    column-gap: ${fluid(7, 7)};

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 16px;
    }
  }

  ${ArticleTagsStyles.Tag} {
    font-size: ${fluid(12, 16)};
    line-height: ${fluid(20, 26)};
    padding-top: ${fluid(5, 8)};
    padding-bottom: ${fluid(5, 8)};
  }

  @media ${from(Device.TabletLarge)} {
    padding-left: 30px;
  }
`;
const TitleBlock = styled.div `
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 5px;
    left: -14px;
    width: ${fluid(4, 7)};
    height: calc(100% - 5px);
    background: var(--baseThemeColour, ${brand.green.default});

    @media ${from(Device.TabletLarge)} {
      left: -30px;
    }
  }
`;
const Title = styled.h2 `
  ${h3};

  margin-bottom: ${fluid(12, 16)};
`;
const Introduction = styled.p `
  ${paragraphRegular};
  ${fonts.proximaNova.semiBold};

  margin-bottom: ${fluid(14, 14)};
`;
const MediaHolder = styled.div `
  ${imageWrapperPreserveRatio(855, 638)};

  overflow: hidden;
  border-radius: 20px 20px 20px 0;
`;
const Image = styled.img `
  ${imageCoverContainer()};
`;
const ContentItem = styled.div `
  margin: ${fluid(32, 48)} 0;
  scroll-margin: calc(var(--headerHeight) + 20px);
`;
const ContentTitle = styled.h3 `
  ${h4};
  margin-bottom: ${fluid(8, 21)};
`;
const Content = styled.div `
  ${contentStyles};

  ul {
    --listColour: ${brand.blue.navy};
    ${fonts.proximaNova.regular};
  }
`;
const HowToGuideStyles = {
    ActiveSvg,
    ButtonList,
    Container,
    Content,
    ContentItem,
    ContentTitle,
    GuideContent,
    GuideContentInner,
    Image,
    IntroBlock,
    Introduction,
    MediaHolder,
    ProgressBar,
    ProgressBarHolder,
    SectionButton,
    SideMenu,
    SideMenuArea,
    SideTitle,
    Title,
    TitleBlock,
};
export default HowToGuideStyles;
